
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerBody,
  DrawerFooter,
  DrawerCloseButton,
  Button,
  Text,
  Flex,
  useToast,
  Box,
  Center,
} from "@chakra-ui/react";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
  Elements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import axios from "axios";
import { API, graphqlOperation } from "aws-amplify";
import { updateBookingSecurityItem } from "../../../../graphql/mutations";
import React, { useEffect, useState } from "react";
import CardConsentNote from "../../../Mini/cardConsentNote";

// Local dev can point payment calls at a local engine via REACT_APP_PAY_API_URL;
// production builds don't set it and fall back to the live API.
const PAY_API = process.env.REACT_APP_PAY_API_URL || "https://api.linkbase.ca";

// Scaffold for TruviCardDrawerContent
const TruviCardDrawerContent = ({
  isOpen,
  onClose,
  stripeAccountId,
  amount,
  currency,
  description,
  guest,
  booking,
  onPaymentSuccess,
}) => {
  const toast = useToast();
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [expiryComplete, setExpiryComplete] = useState(false);
  const [cvcComplete, setCvcComplete] = useState(false);

  // Placeholder for payment logic
  const handlePayment = async () => {
    if (!stripe || !elements) return;

    setLoading(true);

    const cardElement = elements.getElement(CardNumberElement);

    if (!cardElement) {
      toast({
        title: "Input Information",
        description: "Please input card information.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      setLoading(false);
      return;
    }

    try {
      // Create PaymentMethod on connected account
      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: guest.name,
          email: guest.email,
        },
      });

      if (error) {
        toast({
          title: "Card Error",
          description: error.message,
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        setLoading(false);
        return;
      }



      // Call the smart payment endpoint
      try {
        const { Auth } = await import("aws-amplify");
        const session = await Auth.currentSession();
        const idToken = session.getIdToken().getJwtToken();

        // `amount` and `currency` are paired — both originate from the
        // same /payments/smart/quote response (re-fetched on Pay tap by
        // the parent). They MUST travel together: the cents value is
        // denominated in the quote's currency, so sourcing currency
        // from anywhere else (e.g. /pay/getConvertedAmount, which used
        // to be the case) risks charging a CAD-cents amount tagged as
        // USD or vice versa. If you change one, change the other from
        // the same source.
        const response = await axios.post(`${PAY_API}/payments/smart`, {
          bookingId: booking.id,
          paymentMethodId: paymentMethod.id,
          connectedAccountId: stripeAccountId,
          amount: amount.toString(),
          currency: currency.toLowerCase()
        }, {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_LINKBASE_API_KEY,
          },
        });

        if (response.data?.success) {
          // Check if 3D Secure action is required
          if (response.data.requiresAction && response.data.clientSecret) {
            try {
              // Handle 3D Secure authentication
              const confirmResult = await stripe.confirmCardPayment(response.data.clientSecret, {
                payment_method: {
                  card: cardElement,
                  billing_details: {
                    name: guest.name,
                    email: guest.email,
                  },
                },
              });

              if (confirmResult.error) {
                toast({
                  title: "3D Secure Failed",
                  description: confirmResult.error.message,
                  status: "error",
                  duration: 4000,
                  isClosable: true,
                });
                setLoading(false);
                return;
              }

              if (confirmResult.paymentIntent.status !== 'succeeded' && confirmResult.paymentIntent.status !== 'requires_capture') {
                toast({
                  title: "Payment Incomplete",
                  description: `Payment status: ${confirmResult.paymentIntent.status}`,
                  status: "warning",
                  duration: 4000,
                  isClosable: true,
                });
                setLoading(false);
                return;
              }
            } catch (confirmError) {
              console.error("3D Secure confirmation error:", confirmError);
              toast({
                title: "3D Secure Error",
                description: "Failed to complete 3D Secure authentication.",
                status: "error",
                duration: 4000,
                isClosable: true,
              });
              setLoading(false);
              return;
            }
          }

          // Call smart verification endpoint after successful payment
          try {
            const nameParts = guest.name.trim().split(/\s+/);
            const firstName = nameParts[0] || "Guest";
            const lastName = nameParts.slice(1).join(" ") || "Name";
            
            const verificationResponse = await axios.post(`${PAY_API}/security/smart-verification`, {
              bookingId: booking.id,
              guest: {
                firstName: firstName,
                lastName: lastName,
                email: guest.email || "john.doe@example.com",
                telephoneNumber: guest.phone || "12502123000"
              },
              basicAmount: 500,
              extendedAmount: 50000
            }, {
              headers: {
                'Authorization': `Bearer ${idToken}`,
                'Content-Type': 'application/json',
                'x-api-key': process.env.REACT_APP_LINKBASE_API_KEY,
              },
            });

            if (verificationResponse.data?.success) {
              const verificationData = verificationResponse.data.data;
              // Now capture the payment
              try {
                const captureResponse = await axios.post(`${PAY_API}/payments/simple/capture`, {
                  paymentIntentId: response.data.paymentIntentId,
                  connectedAccountId: stripeAccountId
                }, {
                  headers: {
                    'Authorization': `Bearer ${idToken}`,
                    'Content-Type': 'application/json',
                    'x-api-key': process.env.REACT_APP_LINKBASE_API_KEY,
                  },
                });

                if (captureResponse.data?.success) {
                  // Update booking security item with verification data
                  try {
                    const depositSecurityItem = booking.securityItems.items.find(
                      (item) => item.type === "DEPOSIT"
                    );

                    if (depositSecurityItem) {
                      const verificationData = verificationResponse.data.data;
                      const verificationStatus = verificationData.result.verification.status;
                      const verificationId = verificationData.result.verification.verificationId;

                      const bookingSecurityItemUpdateInput = {
                        id: depositSecurityItem.id,
                        status: verificationStatus,
                        isComplete: true,
                        verificationId: verificationId,
                        payIntentId: response.data.paymentIntentId,
                      };

                      const securityItemRes = await API.graphql(
                        graphqlOperation(updateBookingSecurityItem, {
                          input: bookingSecurityItemUpdateInput,
                        })
                      );


                    } else {
                      console.warn("No DEPOSIT security item found for this booking.");
                    }
                  } catch (securityUpdateError) {
                    console.error("Error updating security item:", securityUpdateError);
                  }

                  toast({
                    title: "Payment, Verification & Capture Successful",
                    description: "Payment, verification, and capture have been processed.",
                    status: "success",
                    duration: 4000,
                    isClosable: true,
                  });
                  if (onPaymentSuccess) {
                    onPaymentSuccess(response.data.paymentIntentId);
                  }
                } else {
                  toast({
                    title: "Capture Failed",
                    description: captureResponse.data?.message || "Payment was authorized and verified, but capture failed.",
                    status: "error",
                    duration: 4000,
                    isClosable: true,
                  });
                }
              } catch (captureError) {
                console.error("Capture error:", captureError);
                toast({
                  title: "Capture Error",
                  description: "Payment was authorized and verified, but capture failed.",
                  status: "error",
                  duration: 4000,
                  isClosable: true,
                });
              }
            } else {
              // Verification failed
              toast({
                title: "Verification Failed",
                description: verificationResponse.data?.error || "Verification failed after payment.",
                status: "error",
                duration: 4000,
                isClosable: true,
              });
            }
          } catch (verificationError) {
            console.error("Smart verification error:", verificationError);
            let errorMsg = verificationError?.response?.data?.details?.detail || verificationError?.response?.data?.error || "Verification failed after payment.";
            toast({
              title: "Verification Error",
              description: errorMsg,
              status: "error",
              duration: 4000,
              isClosable: true,
            });
          }
        } else {
          toast({
            title: "Payment Failed",
            description: response.data.message || "Payment processing failed.",
            status: "error",
            duration: 4000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Smart payment error:", error);
        toast({
          title: "Payment Error",
          description: "Failed to process payment.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      }
      
      onClose();
    } catch (err) {
      console.error("Truvi payment error:", err);
      toast({
        title: "Error",
        description: "Something went wrong during payment.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer isOpen={isOpen} placement="bottom" onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent
        minH="35vh"
        borderTopRadius="2xl"
        maxW={{ base: "100%", md: "520px" }}
        mx="auto"
        mb={{ md: 8 }}
        borderRadius={{ md: "24px" }}
      >
        <Flex mx={3} my={4} align="center" justify="space-between">
          <Text fontSize="18px" fontWeight={800}>
            Pay Smart Damage Waiver
          </Text>
          <DrawerCloseButton pos="relative" top={0} right={0} />
        </Flex>
        <DrawerBody p={3}>
          <Text fontWeight={800} fontSize="12px" pb="5px">
            Card Number
          </Text>
          <Box px={3} py={3} borderRadius="md" bg="#3e4757" mb={4} w="full">
            <CardNumberElement
              options={{
                style: {
                  base: {
                    fontSize: "16px",
                    color: "#fff",
                    "::placeholder": { color: "#ccc" },
                    iconColor: "#ccc",
                    fontFamily: "inherit",
                  },
                },
              }}
              onChange={e => setCardComplete(e.complete)}
            />
          </Box>
          <Flex gap={4}>
            <Flex flex={1} flexDirection="column">
              <Text fontWeight={800} fontSize="12px" pb="5px">
                Expiry
              </Text>
              <Box px={3} py={3} borderRadius="md" bg="#3e4757" w="full">
                <CardExpiryElement
                  options={{
                    style: {
                      base: {
                        fontSize: "16px",
                        color: "#fff",
                        "::placeholder": { color: "#ccc" },
                        fontFamily: "inherit",
                      },
                    },
                  }}
                  onChange={e => setExpiryComplete(e.complete)}
                />
              </Box>
            </Flex>
            <Flex flex={1} flexDirection="column">
              <Text fontWeight={800} fontSize="12px" pb="5px">
                CVC
              </Text>
              <Box px={3} py={3} borderRadius="md" bg="#3e4757" w="full">
                <CardCvcElement
                  options={{
                    style: {
                      base: {
                        fontSize: "16px",
                        color: "#fff",
                        "::placeholder": { color: "#ccc" },
                        fontFamily: "inherit",
                      },
                    },
                  }}
                  onChange={e => setCvcComplete(e.complete)}
                />
              </Box>
            </Flex>
          </Flex>
        </DrawerBody>
        <DrawerFooter p={3} flexDirection="column" gap={2}>
          <Button
            onClick={handlePayment}
            isLoading={loading}
            bg="#817efc"
            color="white"
            h="42px"
            w="full"
            variant="solid"
            borderRadius="10px"
            fontSize="14px"
            disabled={!cardComplete || !expiryComplete || !cvcComplete}
            _disabled={{
              opacity: 0.5,
              cursor: "not-allowed",
              bg: "#817efc",
              color: "white",
              pointerEvents: "none"
            }}
          >
            Pay Now
          </Button>
          <CardConsentNote />
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export const TruviCardDrawer = ({
  isOpen,
  onClose,
  stripeAccountId,
  amount,
  currency,
  description,
  guest,
  booking,
  onPaymentSuccess,
}) => {
  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    if (isOpen) {
      const loadConnectedStripe = async () => {
        const stripe = await loadStripe(process.env.REACT_APP_STRIPE_API_KEY, {
          stripeAccount: stripeAccountId
        });
        setStripePromise(stripe);
      };
      loadConnectedStripe();
    }
  }, [isOpen, stripeAccountId]);

  if (!stripePromise) {
    return null;
  }

  return (
    <Elements stripe={stripePromise}>
      <TruviCardDrawerContent
        isOpen={isOpen}
        onClose={onClose}
        stripeAccountId={stripeAccountId}
        amount={amount}
        currency={currency}
        description={description}
        guest={guest}
        booking={booking}
        onPaymentSuccess={onPaymentSuccess}
      />
    </Elements>
  );
}; 